import './index.css';
import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import {
  MdLocalFireDepartment,
  MdTv,
  MdWifi,
  MdLocalParking,
  MdKingBed,
  MdSingleBed,
  MdDry,
  MdDesk,
  MdDevices,
  MdBathtub,
  MdDirectionsCar,
  MdOpacity,
  MdLocalLaundryService,
  MdIron,
} from 'react-icons/md';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig'; // Adjust path to your Firebase config
import { toast } from 'react-hot-toast';

const Amenities = () => {
  // Memoize amenityIconMap to prevent recreation on every render
  const amenityIconMap = useMemo(() => ({
    'Heating': <MdLocalFireDepartment />,
    'Flat Screen TV': <MdTv />,
    'Free Wi-Fi': <MdWifi />,
    'Private Parking': <MdLocalParking />,
    'King Size Bed': <MdKingBed />,
    'Single Bed': <MdSingleBed />,
    'Hair Dryer': <MdDry />,
    'Work Desk & Chair': <MdDesk />,
    'Streaming Access': <MdDevices />,
    'Free Toiletries': <MdBathtub />,
    'Street Parking': <MdDirectionsCar />,
    'Hot Water': <MdOpacity />,
    'Towels': <MdLocalLaundryService />,
    'Iron & Board': <MdIron />,
  }), []); // Empty dependency array since the map is static

  const { slug } = useParams();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch villa-specific amenities from Firestore using slug
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        setLoading(true);
        const villasCollection = collection(db, 'villas');
        const villasSnapshot = await getDocs(villasCollection);
        const villaData = villasSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .find(v => v.title.toLowerCase().replace(/\s+/g, '-').trim() === slug.trim());

        if (villaData && villaData.amenities) {
          // Map amenities array to include icons
          const formattedAmenities = villaData.amenities.map(text => ({
            icon: amenityIconMap[text] || <MdDevices />, // Fallback icon
            text: text,
          }));
          setAmenities(formattedAmenities);
        } else {
          setError('No amenities found for this villa');
          toast.error('No amenities found for this villa', { duration: 4000 });
        }
      } catch (err) {
        console.error('Error fetching amenities:', err);
        setError('Failed to load amenities');
        toast.error('Failed to load amenities. Please try again.', { duration: 4000 });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchAmenities();
    } else {
      setError('Invalid villa slug');
      setLoading(false);
    }
  }, [slug, amenityIconMap]);

  // Handle loading state
  if (loading) {
    return (
      <div className="amenities-container">
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Handle error state or empty amenities
  if (error || amenities.length === 0) {
    return (
      <div className="amenities-container">
        <h2>Amenities Not Available</h2>
        <p>{error || 'No amenities available for this villa.'}</p>
      </div>
    );
  }

  return (
    <div className="amenities-container">
      <div className="amenities-content">
        <h1 className="amenities-title">AMENITIES</h1>
        <div className="amenities-grid">
          {amenities.map((amenity, index) => (
            <div key={index} className="amenity-item">
              <span className="amenity-icon">{amenity.icon}</span>
              <span className="amenity-text">{amenity.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="amenities-image">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80"
          alt="Cozy room interior"
          className="amenities-img"
        />
      </div>
    </div>
  );
};

export default Amenities;